<template>
    <div id="Login">
        <div class="center-box">
            <div class="logo" v-if="brandLogo">
                <img :src="brandLogo" alt="logo" />
            </div>
            <h1>{{ $t('m.loginTitle') }}</h1>
            <div class="third-login-container" :style="{'height':loginContainerHeight}">
                <!-- Google Login Button -->
                <div v-if="showGoogleLogin" v-active-touch :class="['google-btn', { loading: loadingGoogleLoginBtn, disabled: submitting, 'no-after': !loadingGoogleLoginBtn }]">
                    <div class="signin-pseudo">
                        <GoogleIcon/>
                        <span>{{ $t('m.continueGoogle') }}</span>
                    </div>
                    <div ref="googleLoginBtn" class="signin-button" @touchstart="checkAgreePolicy"></div>
                    <div v-if="!agreePolicy" class="signin-blocker" @click="checkAgreePolicy"></div>
                </div>

                <!-- Apple Login Button -->
                <div v-if="showAppleLogin" v-active-touch :class="['apple-btn', { loading: loadingAppleLoginBtn, disabled: submitting, 'no-after': !loadingAppleLoginBtn }]">
                    <div class="signin-pseudo">
                        <AppleIcon/>
                        <span>{{ $t('m.continueApple') }}</span>
                    </div>
                    <div ref="appleLoginBtn" class="signin-button" id="appleid-signin" data-type="sign-in" @touchstart="checkAgreePolicy"></div>
                    <div v-if="!agreePolicy" class="signin-blocker" @click="checkAgreePolicy"></div>
                </div>
    
                <!-- Phone or Email Switch Button -->
                <div v-if="showPhoneEmailSwitch" v-active-touch :class="['phone-email-btn', { disabled: submitting }]" @click="toggleLogin">
                    <div class="signin-pseudo">
                        <EmailIcon v-show="!emailLoginSwitch"/>
                        <PhoneIcon v-show="emailLoginSwitch"/>
                        <span>{{ emailLoginSwitch ? $t('m.phoneLogin') : $t('m.emailLogin') }}</span>
                    </div>
                </div>

                <div v-if="quickLoginEnable" v-active-touch :class="['guest-btn', { disabled: submitting }]" @click="submitQuickLogin">
                    <div class="signin-pseudo">
                        <svg class="guest-icon" viewBox="0 0 1024 1024" aria-hidden="true">
                            <path d="M512 580.936559a290.234596 290.234596 0 1 1 290.234596-290.234596 290.234596 290.234596 0 0 1-290.234596 290.234596z m0-496.343223a203.771794 203.771794 0 1 0 203.771794 203.771794 203.304427 203.304427 0 0 0-203.771794-203.771794z" fill="currentColor"></path>
                            <path d="M95.576449 1024A37.856686 37.856686 0 0 1 57.719763 986.143314a455.682337 455.682337 0 0 1 454.280237-454.280238 648.704701 648.704701 0 0 1 179.936102 37.856687 41.128252 41.128252 0 0 1 26.639891 46.73665 35.98722 35.98722 0 0 1-46.73665 26.63989A582.806025 582.806025 0 0 0 512 607.576449a379.968964 379.968964 0 0 0-378.566864 378.566865 37.856686 37.856686 0 0 1-37.856687 35.98722z" fill="currentColor"></path>
                            <path d="M928.423551 1024a37.856686 37.856686 0 0 1-37.856687-37.856686 378.099498 378.099498 0 0 0-229.009584-348.188042 37.38932 37.38932 0 0 1 30.378822-68.235509 455.21497 455.21497 0 0 1 274.344135 416.423551 37.856686 37.856686 0 0 1-37.856686 37.856686z" fill="currentColor"></path>
                        </svg>
                        <span>{{ $t('m.quickLogin') }}</span>
                    </div>
                </div>
            </div>
            <div :class="isShowOr ? 'divider' : 'divider-pseudo'"><span v-show="isShowOr">{{ $t('m.or') }}</span></div>
            <!-- 邮箱登录 -->
            <input v-if="emailLoginSwitch && emailLoginEnable" v-active-touch class="email-input-container" type="email" v-model="email" name="email" id="email" :placeholder="$t('m.emailAddress')">
            <!-- 手机号登录 -->
            <div class="phone-input-container" v-else-if="phoneLoginEnable">
                <PhoneNumberInput
                    ref="phoneInput"
                    v-model="phone"
                    :placeholder="$t('m.phonePlaceholder')"
                    :default-country-code="countryCode"
                    :locale="$i18n.locale"
                    @update="phoneChange"
                />
            </div>
            <vue-recaptcha v-if="useRecaptcha" ref="login-recaptcha" size="invisible" :sitekey="captchaSiteKey" :loadRecaptchaScript="true" :recaptchaHost="recaptchaHost" @verify="onRecaptchaVerified" @error="onRecaptchaError"/>
            <button
                v-if="emailLoginEnable || phoneLoginEnable"
                v-active-touch
                class="primary-btn"
                style="margin-top: 10px;"
                :disabled="submitting"
                @click="submitLogin"
                :class="{
                    invalid: !loginValid && (!emailLoginSwitch || email.trim() === ''),
                    submitting
                }"
            >
                <span>{{ $t('next') }}</span>
            </button>
            <div :class="['privacy-agreement', { shake: showShakeEffect }]">
                <input type="checkbox" id="privacy-checkbox" v-model="agreePolicy" name="agreePolicy" class="privacy-checkbox" />
                <label for="privacy-checkbox" class="privacy-text" v-html="replaceTranslateContent($t('m.policyAndTerms'))"></label>
            </div>
            <div class="app-store-container" v-if="showMarketSection">
                <a :href="iosMarketLink" class="market-link" target="_blank" v-if="iosMarketLink">
                    <AppleStoreIcon class="market-icon" />
                </a>
                <a :href="androidMarketLink" class="market-link" target="_blank" v-if="androidMarketLink">
                    <GooglePlayIcon class="market-icon" />
                </a>
            </div>
        </div>
        <div v-if="useTurnstile" ref="turnstileContainer" class="turnstile-container" :class="{ interactive: showTurnstileOverlay }"></div>
        <div v-if="showTurnstileOverlay" class="turnstile-overlay"></div>
        <LanguageSelector/>
    </div>
</template>

<script>
import {getVerifyCode, loginOrRegister, getEmailVerifyCode, quickLogin} from "../../api/notLogin";
import VueRecaptcha from 'vue-recaptcha';
import {getRecaptchaHost} from "@/utils/tool";
import { ensureTurnstileLoaded, getTurnstileLanguage } from "@/utils/turnstile";
import LanguageSelector from '../../components/LanguageSelector';
import AppleStoreIcon from '../../components/icons/AppleStoreIcon';
import GooglePlayIcon from '../../components/icons/GooglePlayIcon';
import AppleIcon from '../../components/icons/AppleIcon';
import GoogleIcon from '../../components/icons/GoogleIcon';
import PhoneIcon from '../../components/icons/PhoneIcon';
import EmailIcon from '../../components/icons/EmailIcon';
import PhoneNumberInput from '../../components/forms/PhoneNumberInput';
import { getRouteAfterLogin } from "@/utils/postLoginRedirect";

import { mapGetters } from 'vuex';

export default {
    name: "Login",
    components: {VueRecaptcha, LanguageSelector, AppleStoreIcon, GooglePlayIcon, AppleIcon, GoogleIcon, PhoneIcon, EmailIcon, PhoneNumberInput},
    data() {
        return {
            loadingGoogleLoginBtn: true,
            loadingAppleLoginBtn: true,
            submitting: false, // 提交中
            // 手机登录
            phone: '',
            inputPhoneResult: undefined,
            loginValid: false,
            // 邮箱登录
            email: '',
            emailLoginSwitch: false, // 是否开启邮箱登录
            // 人机验证
            recaptchaHost: getRecaptchaHost(),
            pendingCaptchaAction: undefined,
            turnstileWidgetId: undefined,
            ipCountryCode: '',
            agreePolicy: true,
            showShakeEffect: false,
            showTurnstileOverlay: false,
            GoogleJSLoadFailed: false,
            AppleJSLoadFailed: false,
        }
    },
    created() {
        if (!this.countryCodeFromStore) {
            this.getLocation().then(code => {
                this.ipCountryCode = code
            })
        }
    },
    mounted() {
        // 初始化apple登录
        if (this.appleClientId) {
            this.initAppleLogin()
        }
        // 初始化谷歌登录
        if (this.googleClientId) {
            this.initGoogleLogin()
        }
        // 判断当前域名是否是'qr.heycharge.global', 是则默认开启邮箱登录
        if (window.location.hostname === 'qr.heycharge.global') {
            this.emailLoginSwitch = true;
        }
        this.showLoginRequiredTipFromQuery()
        this.initTurnstile()
    },
    computed: {
        ...mapGetters('appConfig', {
            brandName: 'brand_name',
            brandLogo: 'brand_logo',
            countryCodeFromStore: 'country_code',
            // 隐私协议与服务条款
            privacyPolicyUrl: 'privacy_policy',
            termsOfServiceUrl: 'terms_and_conditions',
            // 允许的登录方式
            loginMethod: 'login_method',
            // 人机验证
            captchaEnabled: 'captcha_enabled',
            captchaProvider: 'captcha_provider',
            captchaSiteKey: 'captcha_site_key',
            // 下载链接
            iosMarketLink: 'ios_market_link',
            androidMarketLink: 'android_market_link'
        }),
        countryCode() {
            // 优先使用 store 的值，其次使用 IP 获取的值
            return this.countryCodeFromStore.toUpperCase() || this.ipCountryCode
        },
        // 判断是否有苹果支付方式，并返回apple_client_id
        appleClientId() {
            const appleMethod = this.loginMethod && this.loginMethod.find(method => method.name === 'apple');
            return appleMethod ? appleMethod.apple_client_id : '';
        },
        // 判断是否有谷歌支付方式，并返回google_client_id
        googleClientId() {
            const googleMethod = this.loginMethod && this.loginMethod.find(method => method.name === 'google');
            return googleMethod ? googleMethod.google_client_id : '';
        },
        // 判断是否允许邮箱登录
        emailLoginEnable() {
            return this.loginMethod && this.loginMethod.find(method => method.name === 'email');
        },
        // 判断是否允许手机号登录
        phoneLoginEnable() {
            return this.loginMethod && this.loginMethod.find(method => method.name === 'phone');
        },
        quickLoginEnable() {
            return this.loginMethod && this.loginMethod.find(method => method.name === 'quick_login');
        },
        hasManualLoginInput() {
            return !!this.emailLoginEnable || !!this.phoneLoginEnable;
        },
        // 判断是否显示or
        isShowOr() {
            return this.hasManualLoginInput && (
                this.showAppleLogin ||
                this.showGoogleLogin ||
                this.showPhoneEmailSwitch ||
                this.quickLoginEnable
            );
        },
        // 登录方式容器高度预计算
        loginContainerHeight() {
            const enabledMethods = [
                this.showAppleLogin,
                this.showGoogleLogin,
                this.showPhoneEmailSwitch,
                this.quickLoginEnable
            ].filter(Boolean).length;

            return enabledMethods > 0 ? `${enabledMethods * 60 - 10}px` : '0px';
        },
        showGoogleLogin() {
            return !!this.googleClientId && !this.GoogleJSLoadFailed;
        },
        showAppleLogin() {
            return !!this.appleClientId && !this.AppleJSLoadFailed;
        },
        showPhoneEmailSwitch() {
            return !!this.emailLoginEnable && !!this.phoneLoginEnable;
        },
        showMarketSection() {
            return !!this.iosMarketLink || !!this.androidMarketLink;
        },
        useTurnstile() {
            return !!this.captchaEnabled && this.captchaProvider === 'turnstile'
        },
        useRecaptcha() {
            return !!this.captchaEnabled && this.captchaProvider === 'recaptcha'
        }
    },
    watch: {
        '$i18n.locale'() {
            if (this.useTurnstile) {
                this.initTurnstile()
            }
        },
        useTurnstile() {
            this.initTurnstile()
        },
        captchaSiteKey() {
            this.initTurnstile()
        }
    },
    beforeDestroy() {
        this.removeTurnstile()
    },
    methods: {
        showLoginRequiredTipFromQuery() {
            if (!this.$route || this.$route.query.loginRequired !== '1') return;

            this.toast(this.$t('m.loginRequiredTip'));
            const query = { ...this.$route.query };
            delete query.loginRequired;
            this.$router.replace({ path: this.$route.path, query }).catch(() => {});
        },
        removeTurnstile() {
            if (this.turnstileWidgetId !== undefined && window.turnstile && typeof window.turnstile.remove === 'function') {
                window.turnstile.remove(this.turnstileWidgetId)
            }
            this.turnstileWidgetId = undefined
        },
        async initTurnstile() {
            if (!this.useTurnstile || !this.captchaSiteKey) {
                this.removeTurnstile()
                return
            }

            await this.$nextTick()

            if (!this.$refs.turnstileContainer) {
                return
            }

            try {
                const turnstile = await ensureTurnstileLoaded(this.$loadScript)
                this.removeTurnstile()
                this.turnstileWidgetId = turnstile.render(this.$refs.turnstileContainer, {
                    sitekey: this.captchaSiteKey,
                    size: 'flexible',
                    execution: 'execute',
                    appearance: 'interaction-only',
                    language: getTurnstileLanguage(this.$i18n && this.$i18n.locale),
                    'before-interactive-callback': () => {
                        this.hideLoading()
                        this.showTurnstileOverlay = true
                    },
                    callback: token => this.onTurnstileVerified(token),
                    'error-callback': () => this.onTurnstileError(),
                    'expired-callback': () => this.onTurnstileExpired(),
                    'timeout-callback': () => this.onTurnstileError(),
                    theme: 'light',
                    action: 'login',
                })
            } catch (error) {
                this.turnstileWidgetId = undefined
            }
        },
        handleCaptchaUnavailable() {
            this.pendingCaptchaAction = undefined
            this.submitting = false
            this.showTurnstileOverlay = false
            this.hideLoading()
            this.toast(this.$t('eMsg.30001'))
        },
        executeTurnstile() {
            const executeWidget = () => {
                if (window.turnstile && typeof window.turnstile.execute === 'function' && this.turnstileWidgetId !== undefined) {
                    window.turnstile.execute(this.turnstileWidgetId)
                    return
                }
                this.handleCaptchaUnavailable()
            }

            if (!window.turnstile || typeof window.turnstile.execute !== 'function' || this.turnstileWidgetId === undefined) {
                this.initTurnstile().then(() => {
                    executeWidget()
                }).catch(() => {
                    this.handleCaptchaUnavailable()
                })
                return
            }

            executeWidget()
        },
        resetTurnstile() {
            this.showTurnstileOverlay = false
            if (window.turnstile && typeof window.turnstile.reset === 'function' && this.turnstileWidgetId !== undefined) {
                window.turnstile.reset(this.turnstileWidgetId)
            }
        },
        onTurnstileVerified(token) {
            this.showTurnstileOverlay = false
            this.onHumanVerified(token)
        },
        onTurnstileError() {
            this.handleCaptchaUnavailable()
        },
        onTurnstileExpired() {
            this.resetTurnstile()
        },
        onRecaptchaVerified(token) {
            this.onHumanVerified(token)
        },
        onRecaptchaError() {
            this.handleCaptchaUnavailable()
        },
        onHumanVerified(verify_token) {
            const action = this.pendingCaptchaAction
            this.pendingCaptchaAction = undefined

            if (action === 'email') {
                return this.requestEmailVerifyCode(verify_token)
            }

            return this.requestLogin(verify_token)
        },
        // 初始化谷歌登录
        async initGoogleLogin() {
            try {
                await Promise.race([
                    this.$loadScript('https://accounts.google.com/gsi/client'),
                    new Promise((_, reject) => setTimeout(() => reject(new Error('Google JS load timeout')), 10000))
                ]);

                window.google.accounts.id.initialize({
                    client_id: this.googleClientId,
                    callback: this.handleGoogleLoginResponse,
                    auto_select: true
                })
                window.google.accounts.id.renderButton(
                    this.$refs.googleLoginBtn, {
                        width: 295,
                        text: 'signin_with',
                    }
                )
                console.log("init google login")
            } catch (error) {
                console.log('init google login error', error)
                // JS加载失败，隐藏Google登录按钮
                this.GoogleJSLoadFailed = true;
            } finally {
                this.loadingGoogleLoginBtn = false;
            }
        },
        // 谷歌登录响应
        handleGoogleLoginResponse(resp) {
            if (!this.agreePolicy) {
                this.checkAgreePolicy({ preventDefault: () => {} });
                return;
            }
            this.handleLoginWithThird({type: 'google', id_token: resp.credential})
        },
        // 初始化apple登录
        async initAppleLogin() {
            try {
                await Promise.race([
                    this.$loadScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'),
                    new Promise((_, reject) => setTimeout(() => reject(new Error('Apple JS load timeout')), 10000))
                ]);
                
                window.AppleID.auth.init({
                    clientId: this.appleClientId,
                    scope: 'email name',
                    redirectURI: window.location.origin,
                    usePopup: true,
                })
                document.addEventListener('AppleIDSignInOnSuccess', (event) => {
                    console.log("AppleIDSignInOnSuccess event: ", event)
                    this.handleAppleLoginResponse(event.detail)
                })
                document.addEventListener('AppleIDSignInOnFailure', (event) => {
                    console.log("AppleIDSignInOnFailure event: ", event)
                    console.log('AppleIDSignInOnFailure: ', event.detail)
                })
                console.log("init apple login")
            } catch (error) {
                console.log('init apple login error', error)
                // JS加载失败，隐藏Apple登录按钮
                this.AppleJSLoadFailed = true;
            } finally {
                this.loadingAppleLoginBtn = false;
            }
        },
        // Apple 登录响应
        handleAppleLoginResponse(resp) {
            if (!this.agreePolicy) {
                this.checkAgreePolicy({ preventDefault: () => {} });
                return;
            }
            let params = {type: 'apple', id_token: resp.authorization.id_token,}
            if (resp.user && resp.user.email) {
                params.email = resp.user.email
            }
            if (resp.user && resp.user.name && resp.user.name.firstName) {
                params.first_name = resp.user.name.firstName
            }
            if (resp.user && resp.user.name && resp.user.name.lastName) {
                params.last_name = resp.user.name.lastName
            }
            this.handleLoginWithThird(params)
        },
        phoneChange(res) {
            // 解决切换国家后自动聚焦的问题
            if (this.inputPhoneResult && this.inputPhoneResult.countryCode !== res.countryCode) {
                this.$nextTick(() => {
                    this.$refs.phoneInput && this.$refs.phoneInput.blurInput && this.$refs.phoneInput.blurInput();
                });
            }
            this.inputPhoneResult = res;
            this.loginValid = res.isValid
        },
        toggleLogin() {
            if (this.submitting) {
                return
            }
            this.emailLoginSwitch = !this.emailLoginSwitch;
            this.email = '';
            this.phone = '';
            this.inputPhoneResult = undefined;
            this.loginValid = false;
        },
        submitLogin() {
            if (!this.agreePolicy) {
                this.triggerShake()
                return this.toast(this.replaceTranslateContent(this.$t('tMsg.agreePolicy')));
            }

            if (this.emailLoginSwitch) {
                // 邮箱登录
                let email = this.email ? this.email.trim() : '';
                const match = /^[a-zA-Z0-9_\-.]+@[a-zA-Z0-9_\-]+(\.[a-zA-Z0-9_\-]+)+$/;
                if (!match.test(email)) {
                    return this.toast(this.$t('tMsg.wrongEmail'));
                }
                if (this.submitting) {
                    return;
                }
                this.submitting = true;
                this.showLoading();
                if (this.captchaEnabled) {
                    this.pendingCaptchaAction = 'email'
                    if (this.useTurnstile) {
                        return this.executeTurnstile()
                    }
                    if (this.useRecaptcha) {
                        return this.$refs["login-recaptcha"] && this.$refs["login-recaptcha"].execute()
                    }
                }
                this.requestEmailVerifyCode()
            } else {
                // 手机登录
                if (!this.inputPhoneResult || !this.inputPhoneResult.isValid) {
                    return this.toast(this.$t('m.errorTel'));
                }
                if (this.submitting) {
                    return;
                }
                this.submitting = true;
                this.showLoading();
                // 如果需要人机验证，调用人机验证，否则直接请求提交
                if (this.captchaEnabled) {
                    this.pendingCaptchaAction = 'phone'
                    if (this.useTurnstile) {
                        return this.executeTurnstile()
                    }
                    if (this.useRecaptcha) {
                        return this.$refs["login-recaptcha"] && this.$refs["login-recaptcha"].execute()
                    }
                } else {
                    this.requestLogin();
                }
            }
        },
        submitQuickLogin() {
            if (!this.agreePolicy) {
                this.triggerShake()
                return this.toast(this.replaceTranslateContent(this.$t('tMsg.agreePolicy')));
            }
            if (this.submitting) {
                return
            }

            const code = this.$store.getters['appConfig/device_code']
            if (!code) {
                return this.toast(this.$t('eMsg.500'))
            }

            this.submitting = true
            this.showLoading()

            quickLogin({ code }).then(res => {
                this.$store.dispatch('setToken', res.data.token)
                return this.$store.dispatch('appConfig/fetchAppConfig')
            }).then(() => {
                this.$store.dispatch('setUserInfoCollection', [])
                this.$store.dispatch('setIsBorrow', 0)
                this.$router.push(getRouteAfterLogin())
            }).catch(er => {
                if (er && er.code) {
                    this.toast(this.$t('eMsg.' + er.code))
                    return
                }
                this.toast(er && er.message)
            }).finally(() => {
                this.submitting = false
                this.hideLoading()
            })
        },
        requestEmailVerifyCode(verify_token) {
            const email = this.email ? this.email.trim() : ''
            const captcha_provider = this.captchaEnabled ? this.captchaProvider : undefined
            getEmailVerifyCode({ email, verify_token, captcha_provider })
                .then(() => {
                    this.$router.push({ name: 'email_code_verify', params: { email } });
                })
                .catch(er => {
                    if (er && er.code) {
                        this.toast(this.$t('eMsg.' + er.code))
                        return
                    }
                    this.toast(er && er.message);
                })
                .finally(() => {
                    this.pendingCaptchaAction = undefined
                    this.submitting = false;
                    this.hideLoading();
                    this.resetTurnstile()
                    this.$refs["login-recaptcha"] && this.$refs["login-recaptcha"].reset()
                });
        },
        requestLogin(verify_token) {
            if (!this.inputPhoneResult || !this.inputPhoneResult.countryCallingCode || !this.inputPhoneResult.nationalNumber) {
                this.submitting = false
                this.hideLoading()
                return this.toast(this.$t('m.errorTel'))
            }
            let tel_code = this.inputPhoneResult.countryCallingCode,
                tel = this.inputPhoneResult.nationalNumber
            const captcha_provider = this.captchaEnabled ? this.captchaProvider : undefined
            this.showLoading()
            getVerifyCode({tel_code, tel, verify_token, captcha_provider}).then(() => {
                this.$router.push({name: 'code_verify', params: {tel, tel_code}})
            }).catch(er => {
                this.toast(this.$t('eMsg.' + er.code));
            }).finally(_ => {
                this.pendingCaptchaAction = undefined
                this.submitting = false
                this.hideLoading()
                this.resetTurnstile()
                this.$refs["login-recaptcha"] && this.$refs["login-recaptcha"].reset()
            })
        },
        // 第3方登录
        handleLoginWithThird(params) {
            this.submitting = true
            let data = {...params, platform: 'web', box_number: this.$store.getters['appConfig/device_code']}
            loginOrRegister(data).then(res => {
                this.$store.dispatch('setToken', res.data.token)
                let uInfoCollection = res.data.user_info_collection,
                    isBorrow = parseInt(res.data.is_borrow)
                this.$store.dispatch('setUserInfoCollection', uInfoCollection)
                this.$store.dispatch('setIsBorrow', isBorrow)
                this.$router.push(getRouteAfterLogin(uInfoCollection))
            }).catch(er => {
                this.toast(this.$t('tMsg.loginError') + ' ' + er.message)
            }).finally(_ => {
                this.submitting = false
            })
        },
        checkAgreePolicy(event) {
            if (!this.agreePolicy) {
                this.toast(this.replaceTranslateContent(this.$t('tMsg.agreePolicy')));
                this.triggerShake()
                event.preventDefault();
            }
        },
        triggerShake() {
            this.showShakeEffect = true;
            setTimeout(() => {
                this.showShakeEffect = false;
            }, 500); // 与 shake 动画持续时间一致
        }
    }
}
</script>

<style lang="scss">
#Login {
    background: #fff;

    .center-box {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        padding-bottom: 46px;
    }

    .logo {
        margin: 0 auto 45px;
        height: 71px;
        width: 145px;

        img {
            height: 100%;
            width: 100%;
            object-fit: contain;
            object-position: center bottom;
        }
    }

    .divider,
    .divider-pseudo {
        display: flex;
        align-items: center;
        text-align: center;
        margin: 30px auto;
        height: 16px;
        width: 295px;
    }

    .divider::before,
    .divider::after {
        content: "";
        flex: 1;
        border-bottom: 0.5px solid #dddddd;
    }

    .divider::before {
        margin-right: 10px;
    }

    .divider::after {
        margin-left: 10px;
    }

    .divider span {
        font-size: 16px;
        color: #999;
        line-height: 13px;
        height: 16px;
    }

    .toggle-login {
        width: 295px;
        margin: 20px auto 0;
        font-size: 14px;
        color: #3183ff;
    }

    .privacy-agreement {
        display: flex;
        align-items: flex-start;
        width: 295px;
        margin: 10px auto 0;
    }
    @keyframes shake {
        0% { transform: translateX(0px); }
        25% { transform: translateX(-10px); }
        50% { transform: translateX(10px); }
        75% { transform: translateX(-10px); }
        100% { transform: translateX(0px); }
    }

    .shake {
        animation: shake 0.5s ease;
    }

    .privacy-checkbox {
        margin-right: 9px;
        margin-top: 3px;
    }

    .privacy-text {
        line-height: 17.28px;
        font-size: 12px;
        color: #333;

        a {
            color: #3183ff;
            text-decoration: underline;
        }
    }

    .app-store-container {
        margin: 20px auto 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 295px;
        .market-link {
            width: 140px;
            img {
                width: 100%;
                height: 100%;
                object-fit: contain;
            }
        }
    }

    .turnstile-container {
        position: fixed;
        width: 300px;
        height: 60px;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 10000;
        pointer-events: none;

        &.interactive {
            pointer-events: auto;
        }
    }

    .turnstile-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.2);
        backdrop-filter: blur(2px);
        z-index: 9999;
    }

    .third-login-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;

        > *:not(:last-child) {
            margin-bottom: 10px;
        }

        .google-btn,
        .apple-btn {
            display: flex;
            position: relative;  
            height: 50px;
            border-radius: 6px;
            user-select: none; /* 禁用文本选择 */

            &.disabled {
                pointer-events: none;
                opacity: 0.72;
            }
            
            &.loading {
                overflow: hidden;
                pointer-events: none;

                .signin-pseudo {
                    border-color: transparent;
                    border-width: 1px;
                    background-clip: padding-box;
                    
                    & > * {
                        opacity: 0.6;
                    }
                }
            }
            &.loading::after {
                content: "";
                position: absolute;
                width: 350px;
                height: 350px;
                top: 50%;
                left: 50%;
                background: #E6E6E6;
                animation: rotate 3s linear infinite;
                transform-origin: 0 0;
                z-index: 0;
            }
            // 解决safari浏览器after伪元素残留问题
            &.no-after::after {
                content: none;
                display: none;
            }
            @keyframes rotate {
                from {
                    transform: rotate(0deg);
                }
                to {
                    transform: rotate(360deg);
                }
            }
        }

        .phone-email-btn,
        .guest-btn {
            position: relative;
            display: flex;
            width: 295px;
            height: 50px;
            cursor: pointer;
            user-select: none; /* 禁用文本选择 */

            &.disabled {
                pointer-events: none;
                opacity: 0.72;
            }
        }

        .touch-active .signin-pseudo {
            background-color: #E3E3E3;
        }

        .guest-icon {
            width: 20px;
            height: 20px;
            color: #545454;
            flex: 0 0 auto;
        }

        .signin-button {
            display: flex;
            align-items: center;
            position: relative;
            width: 295px;
            max-width: 295px;
            height: 50px;
            opacity: 0;
            z-index: 2;
            cursor: pointer;

            &>div>div>div[role="button"]{
                height: 50px;
            }
        }
        .signin-pseudo {
            position: absolute;
            top: 0;
            left: 0;
            width: 295px;
            height: 50px;
            background: #fff;
            z-index: 1;
            box-sizing: border-box;
            border: 1px solid #E6E6E6;
            border-radius: 6px;
            box-shadow: 0px 1px 2.8px 0px #0000001F;
            display: flex;
            align-items: center;
            padding: 0 15px;
            font-size: 14px;
            font-weight: 500;
            color: #333;
            transition: all 0.1s;
            pointer-events: none;

            span {
                flex: 1;
                text-align: center;
            }
        }
        .signin-blocker {
            position: absolute;
            top: 0;
            left: 0;
            width: 295px;
            height: 50px;
            z-index: 3;
        }
    }

    .phone-input-container {
        width: 295px;
        margin: 0 auto;
        position: relative;
        z-index: 10;

        .phone-number-input {
            width: 100%;
        }
    }
    .email-input-container {
        font-size: 14px;
        font-weight: 400;
        color: #333333;
        display: block;
        width: 295px;
        height: 46px;
        margin: 0 auto;
        outline: none;
        border: 1px solid #E6E6E6;
        border-radius: 5px;
        padding: 0 15px;
        &::placeholder {
            font-size: 14px;
            color: #C6C6C6;
        }
    }

    .primary-btn.submitting {
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
        opacity: 0.88;
    }
    // 修改浏览器自动填充电话号码后的高亮样式
    input:-webkit-autofill {
        font-family: 'SF Pro Text';
        font-weight: 400;
        box-shadow: 0 0 0 30px #fff inset !important; /* 修改背景颜色 */
        -webkit-box-shadow: 0 0 0 30px #fff inset !important; /* 修改背景颜色 */
        -webkit-text-fill-color: #333333 !important; /* 修改文本颜色 */
    }
}
</style>
